import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  SxProps,
} from "@mui/material";

interface Column {
  label: string;
  field: string;
  align?: "left" | "right" | "center";
  renderCell?: (row: any) => React.ReactNode;
  labelStyle?: SxProps;
  cellStyle?: SxProps;
}

interface DataTableProps {
  columns: Column[];
  data: any[];
  emptyStateMessage?: string;
  rowsPerPage?: number;
  emptyRows?: number;
  onDelete?: (id: string) => void;
  rowStyle?: (rowIndex: number) => React.CSSProperties;
  onRowClick?: (rowData: any) => void;
  alternateBg?: boolean;
}

const DataTable: React.FC<DataTableProps> = ({
  columns,
  data,
  emptyStateMessage = "No data available",
  rowsPerPage = 8,
  onDelete,
  rowStyle,
  onRowClick,
  alternateBg = false,
}) => {
  const handleRowClick = (row: any) => {
    if (onRowClick) {
      onRowClick(row);
    }
  };

  return (
    <TableContainer component={Paper}>
      <Table>
        {/* Table Head */}
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell
                key={col.field}
                align={col.align || "left"}
                sx={{
                  color: "#B5B7C0",
                  fontSize: "14px",
                  ...(col.labelStyle || {}),
                }}
              >
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        {/* Table Body */}
        <TableBody>
          {data.length > 0 ? (
            data.map((row, index) => {
              const defaultRowStyle: SxProps = {
                backgroundColor:
                  index % 2 === 0 && alternateBg ? "#E9E9E9" : "#ffffff",
              };

              const combinedRowStyle = {
                ...defaultRowStyle,
                ...(rowStyle?.(index) || {}),
              };

              return (
                <TableRow
                  key={index}
                  sx={combinedRowStyle}
                  onClick={() => handleRowClick(row)}
                >
                  {columns.map((col) => (
                    <TableCell
                      key={col.field}
                      align={col.align || "left"}
                      sx={{
                        ...col.cellStyle,
                        borderBottom:
                          rowStyle?.(index)?.borderBottom || undefined,
                      }}
                    >
                      {col.renderCell ? col.renderCell(row) : row[col.field]}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} align="center">
                {emptyStateMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default DataTable;
